import type { MapHitEvent, MapMarker, MapSurface } from '@gpx-spa/geo-domain';
import { htmlSurface, isMarkerHit, textSurface } from '@gpx-spa/geo-domain';
import {
  namedTrackersFromIds,
  type NamedTracker,
} from '@/utils/named-tracker-set';
import {
  deviceIdsFromConvoyClusterId,
  parseConvoyMarkerId,
} from './marker-ids';

export function isConvoyGroupCountPin(marker: MapMarker): boolean {
  const parsed = parseConvoyMarkerId(marker.id);
  if (!parsed || parsed.kind !== 'cluster') return false;
  return deviceIdsFromConvoyClusterId(parsed.clusterId).length >= 2;
}

/** Shipment id to toggle when a convoy count pin or expanded device pin is clicked. */
export function convoyHitToggleShipmentId(event: MapHitEvent): number | null {
  if (event.trigger !== 'click' || !isMarkerHit(event)) return null;
  const parsed = parseConvoyMarkerId(event.target.marker.id);
  if (!parsed) return null;
  if (parsed.kind === 'device') return parsed.shipmentId;
  if (isConvoyGroupCountPin(event.target.marker)) return parsed.shipmentId;
  return null;
}

export type ConvoyHoverShipmentRef = {
  id: number;
  devices?: readonly NamedTracker[] | null;
};

/** Cluster members for the Vue roster overlay. Null unless hovering a group count pin. */
export function convoyGroupHoverTrackers(
  event: MapHitEvent,
  shipments: readonly ConvoyHoverShipmentRef[],
): NamedTracker[] | null {
  if (event.trigger !== 'hover' || !isMarkerHit(event)) return null;
  const marker = event.target.marker;
  if (!isConvoyGroupCountPin(marker)) return null;
  const parsed = parseConvoyMarkerId(marker.id);
  if (!parsed || parsed.kind !== 'cluster') return null;
  const ids = deviceIdsFromConvoyClusterId(parsed.clusterId);
  if (ids.length < 2) return null;
  const shipment = shipments.find((s) => s.id === parsed.shipmentId);
  return namedTrackersFromIds(ids, shipment?.devices ?? []);
}

export function convoyMarkerSurface(event: MapHitEvent): MapSurface | null {
  if (!isMarkerHit(event)) return null;
  if (!parseConvoyMarkerId(event.target.marker.id)) return null;
  if (isConvoyGroupCountPin(event.target.marker)) return null;
  const popup = event.target.marker.popup;
  if (!popup) return null;
  const opts = {
    presentation: event.trigger === 'hover' ? 'tooltip' : 'popup',
  } as const;
  if (event.target.marker.popupHtml) {
    return htmlSurface(popup, opts);
  }
  return textSurface(popup, opts);
}
